import { useMutation } from '@apollo/client';
import { currentDateToIsoString } from '@common/neo.tax';

import {
  QuestionFragment as QuestionFragmentType,
  UpdateCompanyQuestionMutation,
  UpdateCompanyQuestionMutationVariables,
  UPDATE_COMPANY_QUESTION,
  QuestionFragmentDocument,
  QuestionCode,
} from '../graphql';
import { useCurrentCompanyId } from './useCurrentCompanyId';

const UPDATE_COMPANY_QUESTION_REQUEST_KEY = 'UPDATE_COMPANY_QUESTION_REQUEST_KEY';

/**
 * Returns a mutation function suitable for setting a single string value for a
 * Question.
 */
export const useUpdateCompanyQuestion = (
  id: string,
  questionId: string,
): (({
  answerValues,
  questionCode,
  validator,
}: {
  answerValues: string | string[];
  validator?: (value: string) => boolean;
  questionCode?: QuestionCode;
}) => Promise<void>) => {
  const companyId = useCurrentCompanyId();
  const [updateCompanyQuestion, { client }] = useMutation<
    UpdateCompanyQuestionMutation,
    UpdateCompanyQuestionMutationVariables
  >(UPDATE_COMPANY_QUESTION, {
    context: {
      debounceKey: UPDATE_COMPANY_QUESTION_REQUEST_KEY,
      serializationKey: UPDATE_COMPANY_QUESTION_REQUEST_KEY,
      errorToast: {
        title: 'Error updating company information',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
    },
  });

  return async ({
    answerValues: answerValueOrValues,
    questionCode,
    validator,
  }: {
    answerValues: string | string[];
    validator?: (value: string) => boolean;
    questionCode?: QuestionCode;
  }): Promise<void> => {
    const isValid = validator
      ? Array.isArray(answerValueOrValues)
        ? answerValueOrValues.every(validator)
        : validator(answerValueOrValues)
      : true;

    const answerValues = new Set([answerValueOrValues].flat());

    const cachedData = client.readFragment<QuestionFragmentType>({
      id: `Question:${id}`,
      fragment: QuestionFragmentDocument,
    });

    if (!cachedData) {
      return;
    }

    const optimisticResponse = {
      ...cachedData,
      // In the multi-value case, this is wrong--it should actually be
      // `null`--but we also won't be reading it in that case, and this
      // optimistic response will disappear after the mutation anyway.
      answerValue: [...answerValues][0],
      // If this is `undefined`, it has no effect on the cache.
      questionChoices:
        cachedData?.questionChoices?.map((choice) => ({
          ...choice,
          selected: answerValues.has(choice.choiceCode),
        })) ?? null,
    };

    // Write the optimistic response to the cache.
    // This is done here so that the cache is updated
    // even if the mutation didn't get sent because of validation.
    client.cache.recordOptimisticTransaction((cache) => {
      cache.writeFragment({
        id: `Question:${id}`,
        fragment: QuestionFragmentDocument,
        data: optimisticResponse,
      });
    }, id);

    if (!isValid) {
      // If invalid, we don't want to send the mutation.
      return;
    }
    try {
      await updateCompanyQuestion({
        variables: {
          companyId: companyId,
          companyQuestionInput: {
            questionId,
            answeredUtcDatetime: currentDateToIsoString(),
            answerValues: [...answerValues],
          },
        },
        refetchQueries: (): string[] => {
          switch (questionCode) {
            case QuestionCode.HadShortYear:
              return ['GetCompany', 'GetPriorYearRevenues', 'GetQuestionSections'];
            case QuestionCode.FirstYearQreAndPyr:
              return ['GetCompany', 'GetPriorYearRevenues'];
            default:
              return [];
          }
        },
      });
    } catch {
      // Rollback the cache to the previous state.
      client.cache.writeFragment({
        id: `Question:${id}`,
        fragment: QuestionFragmentDocument,
        data: cachedData,
      });
    }
  };
};
